/**
 * MagazineLayout Component
 * Large hero product(s) + smaller grid below
 */

import React from 'react';
import ProductCard from './ProductCard';
import './MagazineLayout.css';

// Hero Product Card - larger version
function HeroProductCard({
  product,
  onClick,
  onAddToCart,
  themeColors,
  formatPrice,
  isNavigating,
}) {
  const {
    primaryColor = '#2563eb',
    borderColor = '#e5e7eb',
    textColor = '#111827',
    mutedColor = '#6b7280',
    bgAlt = '#f9fafb',
    accentColor = '#f59e0b',
    headingFont = 'inherit',
  } = themeColors;

  const imageUrl = getProductImage(product);
  const isOutOfStock = product.quantity <= 0;
  const hasVariants = hasVariablePricing(product);
  const priceRange = hasVariants ? getPriceRange(product) : null;

  const renderPrice = () => {
    if (hasVariants && priceRange) {
      if (priceRange.min === priceRange.max) {
        return formatPrice(priceRange.min);
      }
      return `${formatPrice(priceRange.min)} - ${formatPrice(priceRange.max)}`;
    }
    return formatPrice(product.price);
  };

  const handleActionClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isOutOfStock) return;
    if (hasVariants || !onAddToCart) {
      onClick(product);
      return;
    }
    onAddToCart(product);
  };

  return (
    <div
      className={`mgl-hero-card ${isOutOfStock ? 'mgl-hero-card--out-of-stock' : ''}`}
      onClick={() => onClick(product)}
      style={{
        '--mgl-primary': primaryColor,
        '--mgl-border': borderColor,
        '--mgl-text': textColor,
        '--mgl-muted': mutedColor,
        '--mgl-bg-alt': bgAlt,
        cursor: isNavigating ? 'wait' : 'pointer',
      }}
    >
      <div className="mgl-hero-card__image-wrapper">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className={`mgl-hero-card__image ${isOutOfStock ? 'mgl-hero-card__image--faded' : ''}`}
            loading="lazy"
          />
        ) : (
          <div className="mgl-hero-card__placeholder">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
        )}

        {/* Badges */}
        {isOutOfStock && (
          <span className="mgl-hero-card__badge mgl-hero-card__badge--out-of-stock">
            Out of Stock
          </span>
        )}
        {!isOutOfStock && product.featured && (
          <span className="mgl-hero-card__badge mgl-hero-card__badge--featured" style={{ backgroundColor: accentColor }}>
            Featured
          </span>
        )}
      </div>

      <div className="mgl-hero-card__content">
        <h2 className="mgl-hero-card__name" style={{ fontFamily: headingFont }}>
          {product.name}
        </h2>

        {product.description && (
          <p className="mgl-hero-card__description">
            {product.description}
          </p>
        )}

        <div className="mgl-hero-card__footer">
          <span className="mgl-hero-card__price" style={{ color: primaryColor }}>
            {renderPrice()}
          </span>

          <button
            className="mgl-hero-card__button"
            style={{ '--btn-color': primaryColor }}
            disabled={isOutOfStock}
            type="button"
            onClick={handleActionClick}
          >
            {isOutOfStock ? 'Out of Stock' : hasVariants ? 'Choose Options' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function getProductImage(product) {
  if (product.images?.length > 0) {
    const primary = product.images.find(img => img.isPrimary);
    return primary?.url || product.images[0]?.url;
  }
  return product.imageUrl || null;
}

function hasVariablePricing(product) {
  if (product.variantMatrix?.variants?.length > 0) return true;
  if (product.variations?.length > 0 && product.variations.some(v => v.values?.length > 0)) return true;
  return false;
}

function getPriceRange(product) {
  let prices = [];
  if (product.variantMatrix?.variants?.length > 0) {
    prices = product.variantMatrix.variants.filter(v => v.price != null).map(v => parseFloat(v.price));
  } else if (product.variations?.length > 0) {
    product.variations.forEach(v => {
      if (v.values?.length > 0) {
        v.values.forEach(val => {
          if (val.price != null) prices.push(parseFloat(val.price));
        });
      }
    });
  }
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

function MagazineLayout({
  products,
  onProductClick,
  themeColors,
  formatPrice,
  productsPerRow = 4,
  heroProductCount = 1,
  cardStyle = 'standard',
  isNavigating = false,
  onAddToCart,
  renderAdPlacement,
  adInterval = 6,
}) {
  // Featured products first, then others
  const sortedProducts = [...products].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  const heroProducts = sortedProducts.slice(0, heroProductCount);
  const gridProducts = sortedProducts.slice(heroProductCount);

  return (
    <div className="mgl-container">
      {/* Hero Section */}
      {heroProducts.length > 0 && (
        <div className={`mgl-hero mgl-hero--${heroProductCount}`}>
          {heroProducts.map(product => (
            <HeroProductCard
              key={product.id || product._id}
              product={product}
              onClick={onProductClick}
              onAddToCart={onAddToCart}
              themeColors={themeColors}
              formatPrice={formatPrice}
              isNavigating={isNavigating}
            />
          ))}
        </div>
      )}

      {/* Grid Section */}
      {gridProducts.length > 0 && (
        <div
          className="mgl-grid"
          style={{ '--mgl-columns': productsPerRow }}
        >
          {gridProducts.map((product, index) => (
            <React.Fragment key={product.id || product._id}>
              {renderAdPlacement && index > 0 && index % adInterval === 0 && (
                <div className="mgl-ad-slot">
                  {renderAdPlacement(index)}
                </div>
              )}
              <ProductCard
                product={product}
                onClick={onProductClick}
                themeColors={themeColors}
                formatPrice={formatPrice}
                cardStyle={cardStyle}
                isNavigating={isNavigating}
                onAddToCart={onAddToCart}
              />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default MagazineLayout;
